import { ChangeEvent, FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { toast } from "react-toastify";
import { Resource, ResourceSharingUserModel, ResourceType } from "@app/resources";
import ResourceService from "@app/resources/resourceService";
import ResourceSharingModel from "@app/resources/resourceSharingModel";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { parseQuerystring } from "@app/shared/utility/parseQuerystring";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

enum UserType {
  RESOURCE_ADMIN = "Resource Admin",
  USER = "User",
  BOTH = "Both", //just in case they decide to add this option, it's configured and would just need to be added to the dropdown
}

interface Selection {
  resourceId: number;
  userId: number;
}

enum BulkShareView {
  "Resource",
  "User",
}

interface ResourceSharingLocationState {
  resourceTypes: ResourceType[];
  resourceDescription: string;
  isSingleResource: boolean;
  resourceId: number;
  breadcrumbItems: Breadcrumb[];

}

const BulkSharing: FC<React.PropsWithChildren<RouteComponentProps<unknown, unknown, ResourceSharingLocationState>>> = (props: RouteComponentProps<unknown, unknown, ResourceSharingLocationState>) => {
  const [data, setData] = useState(new Array<ResourceSharingModel>());
  const [userType, setUserType] = useState(UserType.USER);
  const [loadedData, setLoadedData] = useState(false);
  const [redirect, setRedirect] = useState("");
  const [selectedItems, setSelectedItems] = useState(new Array<Selection>());
  const [users, setUsers] = useState(new Array<ResourceSharingUserModel>());
  const [clientUserIds, setClientUserIds] = useState(new Array<number>());
  const [resources, setResources] = useState(new Array<Resource>());
  const [copiedUserIds, setCopiedUserIds] = useState<number[]>([]);
  const [copiedResourceIds, setCopiedResourceIds] = useState<number[]>([]);
  const [view, setView] = useState<BulkShareView>(BulkShareView.Resource);
  const [includeInternalUsers, setIncludeInternalUsers] = useState(false);

  const resourceTypes = typeof props.location.state == "undefined" ? [ResourceType.Project] : props.location.state.resourceTypes;
  const resourceTypeDisplay = typeof props.location.state == "undefined" ? "Project" : props.location.state.resourceDescription.charAt(0).toUpperCase() + props.location.state.resourceDescription.slice(1);
  const isSingleResource = typeof props.location.state == "undefined" ? false : props.location.state.isSingleResource;
  const singleResourceId = typeof props.location.state == "undefined" ? undefined : props.location.state.resourceId;
  const breadcrumbItems = [
    ...(typeof props.location.state == "undefined" ? [resourceTypeDisplay] : props.location.state.breadcrumbItems),
    resourceTypeDisplay + " Sharing"
  ];
  const containerRef = useRef<HTMLDivElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const resourceService = new ResourceService();
  const internalUserDomain = "@questanalytics.com";

  // You can use async functions inside useEffect, but it's a little messy.
  // In this particular case, we're fine using Promise.then instead of await
  useEffect(() => {
    if (!loadedData) {
      if (isSingleResource && singleResourceId) {
        //if resourceId is not undefined, then single resource mode
        resourceService.getShareResourceData(singleResourceId).then(initializateDataCallBack);
      } else {
        resourceService.getSharingMatrixData(resourceTypes).then(initializateDataCallBack);
      }
    }
  }, []); // empty array for the second parameter means this will only run once

  useEffect(() => {
    setLoadedData(false);

    const shared: Selection[] = [];
    const resourceList: Resource[] = [];
    let filteredSharingUsers: ResourceSharingUserModel[] = [];

    data.forEach((d) => {
      filteredSharingUsers = d.resourceSharingUsers.filter((u) =>
        userType === UserType.RESOURCE_ADMIN ? u.isResourceAdmin :
          userType === UserType.USER ? !u.isResourceAdmin : true);

      if (filteredSharingUsers.length) {
        filteredSharingUsers.forEach((u) => {
          if (u.shared) {
            shared.push({
              resourceId: d.resource.resourceId,
              userId: u.userId,
            });
          }
        });

        resourceList.push(d.resource);
      }
    });

    const clientUserIds: number[] = filteredSharingUsers
      .filter(({ email }) => !email?.toLowerCase().endsWith(internalUserDomain))
      .map(user => user.userId);

    setResources(resourceList.sort((a, b) => a.name > b.name ? 1 : -1));
    setUsers(filteredSharingUsers.sort((a, b) => a.fullName > b.fullName ? 1 : -1));
    setClientUserIds(clientUserIds);
    setSelectedItems(shared);
    setLoadedData(true);
  }, [userType]);

  useEffect(() => {
    if (selectedItems.length !== 0 && !includeInternalUsers) {
      const internalUserIds: number[] = users
        .filter(({ email }) => email?.toLowerCase().endsWith(internalUserDomain))
        .map(user => user.userId);

      if (internalUserIds.length !== 0) {
        setSelectedItems(selectedItems.filter(s => !internalUserIds.includes(s.userId)));
      }
    }
  }, [includeInternalUsers]);

  const initializateDataCallBack = (fetchedData: ResourceSharingModel[]) => {
    setData(fetchedData);

    const shared: Selection[] = [];
    const resourceList: Resource[] = [];
    let filteredSharingUsers: ResourceSharingUserModel[] = [];

    fetchedData.forEach((d) => {
      filteredSharingUsers = d.resourceSharingUsers.filter((u) => !u.isResourceAdmin);;

      if (filteredSharingUsers.length) {
        filteredSharingUsers.forEach((u) => {
          if (u.shared) {
            shared.push({
              resourceId: d.resource.resourceId,
              userId: u.userId,
            });
          }
        });

        resourceList.push(d.resource);
      }
    });

    const clientUserIds: number[] = filteredSharingUsers
      .filter(({ email }) => !email?.toLowerCase().endsWith(internalUserDomain))
      .map(user => user.userId);

    setResources(resourceList.sort((a, b) => a.name > b.name ? 1 : -1));
    setUsers(filteredSharingUsers.sort((a, b) => a.fullName > b.fullName ? 1 : -1));
    setClientUserIds(clientUserIds);
    setSelectedItems(shared);
    setLoadedData(true);
  };

  const handleUserTypeChange = async (newUserType: UserType) => {
    setUserType(newUserType);
  };

  const getUserByString = (userString: string) => {
    if (!userString || !(userString.includes("(") && userString.includes(")"))) {
      return undefined;
    }
    const name = userString.split("(")[0].trim();
    const email = userString.split("(")[1].split(")")[0];
    return users.find((u) => u.fullName === name && u.email === email);
  };

  const getUserById = (userId: number) => {
    return users.find((u) => u.userId === userId);
  };

  // calculate which resources are shared with which users
  const usersPerResource = useMemo(() => {
    const newMapping = new Map<number, ResourceSharingUserModel[]>();
    selectedItems.forEach((si) => {
      if (newMapping.has(si.resourceId)) {
        const newList = [...newMapping.get(si.resourceId)];
        newList.push(getUserById(si.userId));
        newMapping.set(si.resourceId, newList);
      } else {
        const newList = [getUserById(si.userId)];
        newMapping.set(si.resourceId, newList);
      }
    });

    return newMapping;
  }, [users, resources, selectedItems]);

  const resourcesPerUser = useMemo(() => {
    const newMapping = new Map<number, Resource[]>();
    selectedItems.forEach((si) => {
      if (newMapping.has(si.userId)) {
        const newList = [...newMapping.get(si.userId)];
        newList.push(resources.find((p) => p.resourceId === si.resourceId));
        newMapping.set(si.userId, newList);
      } else {
        const newList = [resources.find((p) => p.resourceId === si.resourceId)];
        newMapping.set(si.userId, newList);
      }
    });

    return newMapping;
  }, [users, resources, selectedItems]);

  const handleSubmit = async () => {
    submitRef.current.disabled = true;
    submitRef.current.innerHTML = `<div class="spinner-border spinner-border-sm text-muted" style="font-size: 0.8rem;" role="status"><span class="visually-hidden">Submitting...</span></div>`;
    //filter to only affect the users being viewed
    const newData = data.map((d) => {
      d.resourceSharingUsers = d.resourceSharingUsers
        .filter((rsu) => userType === UserType.RESOURCE_ADMIN ? rsu.isResourceAdmin : userType === UserType.USER ? !rsu.isResourceAdmin : true);
      return d;
    }).filter((d) => d.resourceSharingUsers.length > 0);

    // unshare everything and then share the right ones
    // this seems more straightforward than diffing the two
    newData.forEach((d) => d.resourceSharingUsers.forEach((drsu) => drsu.shared = false));
    selectedItems.forEach((nsi) => {
      const resource = newData.find((d) => d.resource.resourceId === nsi.resourceId);
      const user = resource.resourceSharingUsers.find((rsu) => rsu.userId === nsi.userId);
      user.shared = true;
    });
    // doesn't return anything on success
    await resourceService.bulkShare(newData);

    const query = parseQuerystring(props.location.search);
    toast(<span>Sharing preferences updated! You&apos;ll be redirected in a few seconds...</span>,
      {
        containerId: "z-index-9999",
        onClose: () => {
          if (query.redirectUrl) {
            setRedirect(query.redirectUrl);
          } else {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0");
          }
        },
      },
    );
  };

  const handleResourceChange = (e: ChangeEvent<HTMLInputElement>, resourceId: number) => {
    const user = getUserByString((e.target as HTMLInputElement).value);
    if (!user) {
      return;
    }

    if (!isAlreadyShared(user.userId, resourceId)) {
      const newSelectedItems = [...selectedItems];
      newSelectedItems.push({
        resourceId,
        userId: user.userId,
      });
      setSelectedItems(newSelectedItems);
      (e.target as HTMLInputElement).value = "";
    }
  };

  const handleUserChange = (e: ChangeEvent<HTMLInputElement>, userId: number) => {
    // We enforce projects having unique names, so this works
    const resource = resources.find((p) => p.name === (e.target as HTMLInputElement).value);
    if (!resource) {
      return;
    }

    if (!isAlreadyShared(userId, resource.resourceId)) {
      const newSelectedItems = [...selectedItems];
      newSelectedItems.push({
        resourceId: resource.resourceId,
        userId,
      });
      setSelectedItems(newSelectedItems);
      (e.target as HTMLInputElement).value = "";
    }
  };

  const isAlreadyShared = (userId: number, resourceId: number) => {
    return selectedItems.some((si) => si.userId === userId && si.resourceId === resourceId);
  };

  const removeShare = (userId: number, resourceId: number) => {
    const newSelectedItems = [...selectedItems];
    const indexToRemove = newSelectedItems.findIndex((nsi) => nsi.userId === userId && nsi.resourceId === resourceId);
    if (indexToRemove >= 0) {
      newSelectedItems.splice(indexToRemove, 1);
      setSelectedItems(newSelectedItems);
    }
  };

  const shareWithAllClients = (resourceId: number) => {
    const selectedSet = new Set(
      selectedItems.map(({ resourceId, userId }) => `${resourceId}-${userId}`)
    );

    const newSelectedItems = clientUserIds
      .filter(userId => !selectedSet.has(`${resourceId}-${userId}`))
      .map(userId => ({ resourceId, userId }));

    setSelectedItems([...selectedItems, ...newSelectedItems]);
  };

  const shareAllResources = (userId: number) => {
    let newSelectedItems = [...selectedItems];
    newSelectedItems = newSelectedItems.filter((nsi) => nsi.userId !== userId);
    newSelectedItems.push(...resources.map((p) => ({ resourceId: p.resourceId, userId })));
    setSelectedItems(newSelectedItems);
  };

  const unshareFromAll = (resourceId: number) => {
    let newSelectedItems = [...selectedItems];
    newSelectedItems = newSelectedItems.filter((nsi) => nsi.resourceId !== resourceId);
    setSelectedItems(newSelectedItems);
  };

  const unshareAllResources = (userId: number) => {
    let newSelectedItems = [...selectedItems];
    newSelectedItems = newSelectedItems.filter((nsi) => nsi.userId !== userId);
    setSelectedItems(newSelectedItems);
  };

  const handleJumpToResource = (e: ChangeEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    // This should only really trigger for the blank option
    if (!value) {
      return;
    }

    const resource = resources.find((p) => p.name === value);
    if (!resource) {
      return;
    }

    // This event shouldn't be callable unless this ref has also been rendered, so we're not worried about nullchecking it
    const el = containerRef.current.querySelector(`#resource-${resource.resourceId}`) as HTMLElement;
    // there's an overload of scrollTo that allows smooth scrolling,
    // but IE doesn't support it and I'm not sure what the proper fallback
    // usage/experience is
    // Plus it could be disorienting on pages with hundreds of projects...

    // the browser drops the value of e, before this setTimeout resolves
    // so we save the target value here to keep it accessible
    const target = e.target;
    window.scrollTo(0, el.offsetTop - 120);
    el.classList.add("highlight");
    window.setTimeout(() => {
      el.classList.remove("highlight");
      target.value = "";
    }, 600);
  };

  const handleJumpToUser = (e: ChangeEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    if (!value) {
      return;
    }

    const user = users.find((u) => `${u.fullName} (${u.email})` === value);
    if (!user) {
      return;
    }

    const el = containerRef.current.querySelector(`#user-${user.userId}`) as HTMLElement;
    const target = e.target;
    window.scrollTo(0, el.offsetTop - 120);
    el.classList.add("highlight");
    window.setTimeout(() => {
      el.classList.remove("highlight");
      target.value = "";
    }, 600);
  };

  const handleResourceViewCopy = (resourceId: number) => {
    const usersForResource = usersPerResource.get(resourceId);
    setCopiedUserIds(usersForResource.map((ufp) => ufp.userId));
  };

  const handleResourceViewPaste = (resourceId: number) => {
    let copiedUsers = copiedUserIds.map((cui) => users.find((u) => u.userId === cui));
    const currentUsers = [...(usersPerResource.get(resourceId) || [])];
    // filter down to the users that aren't already added to this resource
    copiedUsers = copiedUsers.filter((copy) => !currentUsers.find((cu) => cu.userId === copy.userId));
    const newSelectedItems = [...selectedItems];
    copiedUsers.forEach((copy) => {
      newSelectedItems.push({
        resourceId,
        userId: copy.userId,
      });
    });
    setSelectedItems(newSelectedItems);
  };

  const handleUserViewCopy = (userId: number) => {
    const resourcesSharedWithUser = resourcesPerUser.get(userId);
    setCopiedResourceIds(resourcesSharedWithUser.map((pswu) => pswu.resourceId));
  };

  const handleUserViewPaste = (userId: number) => {
    let copiedResources = copiedResourceIds.map((cpi) => resources.find((p) => p.resourceId === cpi));
    const currentResources = [...(resourcesPerUser.get(userId) || [])];
    copiedResources = copiedResources.filter((copy) => !currentResources.find((cp) => cp.resourceId === copy.resourceId));
    const newSelectedItems = [...selectedItems];
    copiedResources.forEach((copy) => {
      newSelectedItems.push({
        resourceId: copy.resourceId,
        userId,
      });
    });
    setSelectedItems(newSelectedItems);
  };

  const handleChangeView = (newView: BulkShareView) => {
    setCopiedResourceIds([]);
    setCopiedUserIds([]);
    setView(newView);
  };

  const renderResourceView = () => {
    return resources.map((resource) => {
      const usersThisResourceIsSharedWith = usersPerResource.get(resource.resourceId) || [];

      const sharedUserSet = new Set(usersThisResourceIsSharedWith.map(user => user.userId));
      const allClientsSelected: boolean = clientUserIds.every(userId => sharedUserSet.has(userId))
      const notSharedUsers = filteredUsers.filter((u) => !sharedUserSet.has(u.userId));

      return (
        <BulkSharingBlock
          allClientsSelected={allClientsSelected}
          allSelected={notSharedUsers.length === 0}
          canPaste={copiedUserIds.length > 0}
          handleCopy={handleResourceViewCopy}
          handleInput={handleResourceChange}
          handlePaste={handleResourceViewPaste}
          handleSelectAll={shareWithAllClients}
          handleSelectNone={unshareFromAll}
          handleUnshare={removeShare}
          id={resource.resourceId}
          isSingleResource={isSingleResource}
          key={resource.resourceId}
          listAvailable={notSharedUsers.map((u) => `${u.fullName} (${u.email})`)}
          listSelected={usersThisResourceIsSharedWith}
          resourceType={resourceTypeDisplay}
          title={resource.name}
          type={BulkShareView.Resource}
        />
      );
    });
  };

  const renderUserView = () => {
    return users.map((user) => {
      const resourcesThisUserHasAccessTo = resourcesPerUser.get(user.userId) || [];

      return (
        <BulkSharingBlock
          allClientsSelected={false}
          allSelected={resourcesThisUserHasAccessTo.length === resources.length}
          canPaste={copiedResourceIds.length > 0}
          handleCopy={handleUserViewCopy}
          handleInput={handleUserChange}
          handlePaste={handleUserViewPaste}
          handleSelectAll={shareAllResources}
          handleSelectNone={unshareAllResources}
          handleUnshare={removeShare}
          id={user.userId}
          isSingleResource={isSingleResource}
          key={user.userId}
          listAvailable={resources.filter((p) => !(resourcesThisUserHasAccessTo.find((ut) => ut.resourceId === p.resourceId))).map((p) => p.name)}
          listSelected={resourcesThisUserHasAccessTo}
          resourceType={resourceTypeDisplay}
          title={`${user.fullName} (${user.email})`}
          type={BulkShareView.User}
        />
      );
    });
  };

  const allClientUsersHaveAllResources = useMemo(() => {
    // should return false on the first instance of a resource not being shared with any client user
    if (selectedItems.length === 0) {
      return false;
    }

    const selectedSet = new Set(
      selectedItems.map((si) => `${si.resourceId}-${si.userId}`)
    );

    if (clientUserIds.length == 0) {
      return false;
    }

    for (const resource of resources) {
      for (const userId of clientUserIds) {
        if (!selectedSet.has(`${resource.resourceId}-${userId}`)) {
          return false;
        }
      }
    }

    return true;
  }, [selectedItems]);

  const handleShareAllWithAllClientsChange = () => {
    if (clientUserIds.length === 0) {
      return;
    }

    if (allClientUsersHaveAllResources) {
      setSelectedItems(selectedItems.filter(s => !clientUserIds.includes(s.userId)));
    } else {
      const selectedSet = new Set(
        selectedItems.map(({ resourceId, userId }) => `${resourceId}-${userId}`)
      );

      const newSelectedItems = [
        ...selectedItems,
        ...resources.flatMap(resource =>
          clientUserIds
            .filter(userId => !selectedSet.has(`${resource.resourceId}-${userId}`))
            .map(userId => ({ resourceId: resource.resourceId, userId }))
        )
      ];

      setSelectedItems(newSelectedItems);
    }
  };

  const handleIncludeInternalUsersChange = () => {
    setIncludeInternalUsers(!includeInternalUsers);
  };

  const filteredUsers = useMemo(() => {
    if (!includeInternalUsers) {
      return users.filter(({ userId }) => clientUserIds.includes(userId));
    }

    return users;
  }, [includeInternalUsers, users]);

  const noClientUsersToShareWith = useMemo(() => {
    return filteredUsers.length === 0 || !filteredUsers.some(({ userId }) => clientUserIds.includes(userId));
  }, [filteredUsers]);

  return (
    <Page documentTitle={`${resourceTypeDisplay} Sharing`}>
      {redirect !== "" ? <Redirect to={redirect} push={true} /> : <Fragment />}
      <Breadcrumbs items={breadcrumbItems} />
      <PageInner>
        <h1 className="mt-5">{resourceTypeDisplay} Sharing</h1>
        <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN]}>
          <div>
            <QESSelect
              label="User Type"
              id="userTypeDropdown"
              value={userType}
              onChange={(e) => handleUserTypeChange(e.target.value as UserType)}
              testId="bulk-sharing__user-type"
              containerClassName="col-2"
            >
              <option key={UserType.RESOURCE_ADMIN} value={UserType.RESOURCE_ADMIN}>{UserType.RESOURCE_ADMIN}</option>
              <option key={UserType.USER} value={UserType.USER}>{UserType.USER}</option>
            </QESSelect>
          </div>
        </RoleRestricted>
        {loadedData && !!users.length && !!resources.length && !isSingleResource && (
          <a className="qes-button qes-button-secondary d-inline-block ml10 mt-2" type="button" href={`${window.uiEndpoint}/home/downloadsharedprojects`}>
            <QesIcon iconName={QesIcons.DownloadCloudV1} isDecorative className="align-text-top me-1" /> Download Shared {resourceTypeDisplay.toLowerCase()}
          </a>
        )}
        {!loadedData ? <LoadingSpinner /> : (
          <>
            <QESCheckbox
              containerClassName="ml10"
              label={`Share all ${resourceTypeDisplay.toLowerCase()}s with all client users`}
              className="mt-0"
              checked={allClientUsersHaveAllResources}
              disabled={noClientUsersToShareWith}
              id="fullShare"
              onChange={handleShareAllWithAllClientsChange}
              testId="bulk-sharing__share-all"
            />
            <RoleRestricted allowedRoles={[Roles.QUEST_INTERNAL_PROJECT_SHARING_USER]}>
              <QESCheckbox
                containerClassName="ml10"
                label={"Include internal users"}
                className="mt-0"
                checked={includeInternalUsers}
                disabled={users.length === 0}
                id="includeInternalUsers"
                onChange={handleIncludeInternalUsersChange}
                testId="bulk-sharing__include_internal_users"
              />
            </RoleRestricted>
            <div className="bulk-sharing__action-bar col-12 mb-3">
              {isSingleResource ? null : (
                <div className="bulk-sharing__jump-to form-group col-3 mb-3">
                  {view === BulkShareView.Resource && resources.length > 0 ? (
                    <>
                      <QESTextInput
                        label={"Jump to " + resourceTypeDisplay.toLowerCase()}
                        list="resource-list"
                        onChange={(e) => handleJumpToResource(e)}
                        testId="bulk-sharing__jump-to-resource"
                      />
                      <datalist id="resource-list">
                        {resources.map((item, index) => <option key={index} value={item.name} />)}
                      </datalist>
                    </>
                  ) : users.length > 0 && (
                    <>
                      <QESTextInput
                        label="Jump to User"
                        list="resource-list"
                        onChange={(e) => handleJumpToUser(e)}
                        testId="bulk-sharing__jump-to-resource"
                      />
                      <datalist id="resource-list">
                          {users.map((item, index) => <option key={index} value={`${item.fullName} (${item.email})`} />)}
                      </datalist>
                    </>
                  )}
                </div>
              )}
              <div className="form-group d-inline-flex justify-content-end col-9 align-items-center">
                {isSingleResource ? null : (
                  <>
                    <QESButton onClick={() => view === BulkShareView.User && handleChangeView(BulkShareView.Resource)} qesType={view === BulkShareView.Resource ? "primary" : "secondary"} qesTestId="bulk-sharing-project-view">{resourceTypeDisplay} View</QESButton>
                    <QESButton onClick={() => view === BulkShareView.Resource && handleChangeView(BulkShareView.User)} qesType={view === BulkShareView.User ? "primary" : "secondary"} qesTestId="bulk-sharing-user-view">User View</QESButton>
                  </>
                )}
                <div className="form-group offset-1 mb-0">
                  <QESButton qesTestId="bulk-sharing_submit-changes" qesRef={submitRef} onClick={handleSubmit}>Submit Changes</QESButton>
                </div>
              </div>
            </div>
            <div ref={containerRef}>
              {!!resources.length && !!users.length && (
                view === BulkShareView.Resource ?
                  renderResourceView() :
                  renderUserView()
              )}
            </div>
            {loadedData && !(!!users.length && !!resources.length) && <p>You either don&apos;t own any {resourceTypeDisplay.toLowerCase()}s to share, or you have no one to share them with.</p>}
          </>
        )}
      </PageInner>
    </Page >
  );
};

export default BulkSharing;

interface BulkSharingBlockProps {
  // whether all resources/users are already selected
  allClientsSelected: boolean;
  allSelected: boolean;
  canPaste: boolean;
  handleCopy: (id: number) => void;
  handleInput: (e: ChangeEvent, id: number) => void;
  handlePaste: (id: number) => void;
  handleSelectAll: (id: number) => void;
  handleSelectNone: (id: number) => void;
  handleUnshare: (userId: number, resourceId: number) => void;
  // the list of available users or resources
  listAvailable: string[];
  // the list of already-selected users or resources
  //listSelected: Array<{ title: string; id: number; }>;
  listSelected: ResourceSharingUserModel[] | Resource[];
  // resource id or user id
  id: number;
  // user name and email or resource name
  title: string;
  type: BulkShareView;
  isSingleResource: boolean;
  resourceType: string;
}

const BulkSharingBlock = (props: BulkSharingBlockProps) => {
  // state is changed internally
  const [open, setOpen] = useState(false);

  // these shouldn't change, and useRef lets them stay consistent between renders so the calculations don't happen again
  const type = useRef(props.type === BulkShareView.Resource ? "resource" : "user");
  const oppositeTypeCapitalized = useRef(type.current === "resource" ? "Users" : "Resources");
  const listLabel = useRef(type.current === "resource" ? "Users this " + props.resourceType.toLowerCase() + " is shared with " : props.resourceType.toLowerCase() + " shared with this user ");
  const emptyLabel = useRef(type.current === "resource" ? "This " + props.resourceType.toLowerCase() + " is not shared with any users." : "No " + props.resourceType.toLowerCase() + "'s are being shared with this user.");

  // this can change, but we also don't need to calculate it for every single render
  const smallText = useMemo(() => (type.current === "resource" ?
    props.allSelected ?
      "This " + props.resourceType.toLowerCase() + " is shared with all available users." :
      "Select a user from the list."
    : props.allSelected ?
      "This user has access to all available " + props.resourceType.toLowerCase() + "." :
      "Select a " + props.resourceType.toLowerCase() + " from the list."
  ), [type.current, props.allSelected]);

  const selectedItems = useMemo(() => {
    if (props.type === BulkShareView.Resource) {
      return (props.listSelected as ResourceSharingUserModel[]).map((item) => ({ id: item.userId, title: `${item.fullName} (${item.email})` }));
    }

    return (props.listSelected as Resource[]).map((item) => ({ id: item.resourceId, title: item.name }));
  }, [props.listSelected]);

  const block = useMemo(() => (
    <div key={props.id} className="bulk-sharing__resource-block col-12 border-bottom pb-4 pt-4" id={`${type.current}-${props.id}`}>
      <h4>{props.title}</h4>
      <div className="row align-items-center">
        <QESTextInput
          disabled={props.allSelected}
          containerClassName="col-3 mb-1"
          label={`${oppositeTypeCapitalized.current}`}
          list={props.id.toString()}
          onChange={(e) => props.handleInput(e, props.id)}
          descriptiveText={smallText}
          testId={`bulk-sharing-${props.id}`}
        />
        <datalist id={props.id.toString()}>
          {props.listAvailable.map((item, index) => <option key={index} value={item} />)}
        </datalist>
        <QESButton onClick={() => props.handleSelectAll(props.id)} qesTestId={`bulk-sharing__share-with-all-${props.id}`} disabled={type.current === "resource" ? props.allClientsSelected : props.allSelected} qesType="secondary" className="col-auto">{props.type === BulkShareView.Resource ? "Share with all client users" : "Share All"}</QESButton>
        <QESButton onClick={() => props.handleSelectNone(props.id)} qesTestId={`bulk-sharing__unshare-from-all-${props.id}`} disabled={props.listSelected.length === 0} qesType="secondary" className="col-auto">Unshare {props.type === BulkShareView.Resource && "from"} All</QESButton>
        {!props.isSingleResource ? <QESButton onClick={() => props.handleCopy(props.id)} qesTestId={`bulk-sharing__copy-${props.id}`} disabled={props.listSelected.length === 0} qesType="secondary" className="col-auto">Copy {oppositeTypeCapitalized.current}</QESButton> : null}
        {!props.isSingleResource ? <QESButton onClick={() => props.handlePaste(props.id)} qesTestId={`bulk-sharing__paste-${props.id}`} disabled={!props.canPaste} qesType="secondary" className="col-auto">Paste {oppositeTypeCapitalized.current}</QESButton> : null}
      </div>
      <div className="">
        {props.listSelected.length > 0 ? (
          <>
            <QESButton qesType="secondary" className="accordion-expansion-button text-dark fw-normal" qesTestId={`bulk-sharing__accordion-${props.id}`} onClick={() => setOpen(!open)} aria-pressed={open}>
              <span className="collapse-indicator me-3">
                <QesIcon iconName={open ? QesIcons.ChevronDown : QesIcons.ChevronRight} description={open ? "Hide users" : "Show users"} height="18px" className="align-text-top" />
              </span>
              {listLabel.current}({props.listSelected.length}):
            </QESButton>
            <div>
              {open && selectedItems.map((item) => (
                <span key={item.id} className="waffles-filters__filter ms-2 mb-1 d-inline-block">
                  {item.title}
                  <QesIcon
                    iconName={QesIcons.XCircle}
                    onClick={() => props.type === BulkShareView.Resource ? props.handleUnshare(item.id, props.id) : props.handleUnshare(props.id, item.id)}
                    description="Remove"
                    className="bulk-sharing__remove-user-icon-button align-text-top ms-2" />
                </span>
              ))}
            </div>
          </>
        ) : <>{emptyLabel.current}</>}
      </div>
    </div>
  ), [props.allSelected, props.allClientsSelected, props.canPaste, props.listAvailable, props.listSelected, open]);

  return block;
};
